import { lastValueFrom } from 'rxjs'

import { getAgenticBaseUrl } from 'utils/agenticUrl'
import { deleteRest, get, post, put } from 'utils/rxfetch'
import { buildURL } from 'utils/urls'

export default () => ({
  fetchIncidentsCount(companyId = '', dateFilter = null, state = 'open', feDateRange = null) {
    // Keep companyFilter on the path even when empty (buildURL would drop '').
    // Custom range is the same [startIso, endIso] pair the incidents list POST
    // sends as feDateRange; buildURL encodes the array as a comma-separated query value.
    return get(
      buildURL(
        `/members-api/incidents/count/?state=${state}&dateFilter=${dateFilter}&companyFilter=${companyId}`,
        feDateRange ? { feDateRange } : {}
      )
    )
  },
  changeReportState(reportId, newState, reportTypePrefix) {
    return post(`/members-api/irontraps/reports/${reportTypePrefix}/${reportId}/${newState}`)
  },
  saveCommentAndTags(reportId, body, reportTypePrefix, companyId = '') {
    return put(
      `/members-api/irontraps/reports/${reportTypePrefix}/${reportId}/comments?companyFilter=${companyId}`,
      body
    )
  },
  getReportedIncidents(
    page,
    search,
    generalFilter,
    reportedByFilter,
    quickFilters,
    companyId = '',
    feDateRange = null,
    sortingBy = null,
    sortDirection = null
  ) {
    return get(
      buildURL('/members-api/irontraps/history/reports', {
        page,
        search,
        generalFilter,
        quickFilters,
        companyFilter: companyId,
        reportedByFilter,
        ...(feDateRange ? { feDateRange } : {}),
        // Omitted when unsorted so the endpoint keeps its default -created order.
        ...(sortingBy ? { sortingBy, sortDirection: sortDirection || 'desc' } : {}),
      })
    )
  },
  getRemediatedIncidents(
    page,
    search,
    generalFilter,
    reportedByFilter,
    feDateRange,
    companyId = '',
    sortingBy = null,
    sortDirection = null
  ) {
    return post(
      buildURL('/members-api/irontraps/history/remediations', {
        companyFilter: companyId,
      }),
      {
        feDateRange,
        reportedByFilter,
        page,
        search,
        generalFilter,
        // Omitted when unsorted so the endpoint keeps its legacy
        // reverse-based -mitigation_date order.
        ...(sortingBy ? { sortingBy, sortDirection: sortDirection || 'desc' } : {}),
      }
    )
  },
  getReport(reportId, reportTypePrefix) {
    return get(`/members-api/irontraps/incidents/${reportTypePrefix}/${reportId}`)
  },
  getAtoIncident(incidentId, page, filters) {
    return get(
      buildURL(`/members-api/irontraps/incidents/ato/${incidentId}`, {
        page,
        ...filters,
      })
    )
  },
  changeAtoIncidentState(incidentId, newState, remediationActions) {
    // Normalize: array [3] -> 3, false/empty -> null
    const action =
      Array.isArray(remediationActions) && remediationActions.length > 0
        ? remediationActions[0]
        : null

    return post(`/members-api/irontraps/incident/atoaction/${incidentId}`, {
      state: newState,
      action,
    })
  },
  getIncidentsList(
    state,
    search,
    page,
    firstPageLoadDate,
    lastRowDate,
    sortingBy,
    generalFilter,
    quickFilters,
    dateFilter,
    detectedByFilter,
    includesFilter,
    excludesFilter,
    assigneeFilter,
    resolvedFilter,
    challengedTypeFilter,
    companyId = '',
    sortDirection = 'desc',
    feDateRange = null,
    themisVerdictFilter = [],
    challengeSourceFilter = []
  ) {
    return post(
      buildURL('/members-api/irontraps/incidents', {
        companyFilter: companyId,
      }),
      {
        state,
        search,
        page,
        firstPageLoadDate,
        lastRowDate,
        sortingBy,
        sortDirection,
        generalFilter,
        quickFilters,
        dateFilter,
        detectedByFilter,
        includesFilter,
        excludesFilter,
        assigneeFilter,
        resolvedFilter,
        challengedTypeFilter,
        ...(themisVerdictFilter.length ? { themisVerdictFilter } : {}),
        ...(challengeSourceFilter.length ? { challengeSourceFilter } : {}),
        ...(feDateRange ? { feDateRange } : {}),
      }
    )
  },
  getServerScanData(reportId, companyId = '') {
    return get(`/members-api/irontraps/serverscan/${reportId}?companyFilter=${companyId}`)
  },
  getScannerDetail(element) {
    return get(buildURL('/members-api/irontraps/scanner-detail', { element }))
  },
  getAffectedMailboxesDetail(reportId, body, isCsv = false, companyId = '') {
    const responseType = isCsv ? 'text' : 'json'
    return post(
      `/members-api/irontraps/affmailboxes-detail/${reportId}?isCsv=${isCsv}&companyFilter=${companyId}`,
      body,
      {},
      responseType
    )
  },
  updateElements(reportId, elementsName, reportTypePrefix) {
    return post(`/members-api/irontraps/scan/${reportTypePrefix}/${elementsName}/${reportId}`, {})
  },
  fetchElements(reportId, elementsName, reportTypePrefix) {
    return get(
      `/members-api/irontraps/reports/${reportTypePrefix}/${reportId}/scan?elementsName=${elementsName}`
    )
  },
  clusterUndo(body) {
    return post(`/members-api/irontraps/uncluster`, body)
  },
  revertBackToOrigin(body) {
    return post(`/members-api/irontraps/recluster`, body)
  },
  fetchTags(reportId, state, reportTypePrefix) {
    return get(`/members-api/irontraps/reports/tags/${reportTypePrefix}/${reportId}/${state}`)
  },
  editTags(reportId, type, tags, reportTypePrefix) {
    return put(
      `/members-api/irontraps/reports/resolutiontags/${reportTypePrefix}/${reportId}/${type}`,
      { tags }
    )
  },
  validateNewAffectedMailboxesSeen() {
    return post('/members-api/irontraps/new-aff-mailboxes-seen')
  },
  fetchReportComments(reportId, community, reportTypePrefix) {
    return get(
      `/members-api/irontraps/reports/${reportTypePrefix}/${reportId}/comments?community=${community}`
    )
  },
  editReportComment(reportId, commentId, body, reportTypePrefix) {
    return post(
      `/members-api/irontraps/reports/${reportTypePrefix}/${reportId}/comments?commentId=${commentId}`,
      body
    )
  },
  stopRemediation(body, companyId) {
    return post(`/members-api/irontraps/stop-clustering?companyFilter=${companyId}`, body)
  },
  deleteReportComment(reportId, commentId, reportTypePrefix) {
    return deleteRest(
      `/members-api/irontraps/reports/${reportTypePrefix}/${reportId}/comments?commentId=${commentId}`
    )
  },
  addReportComment(reportId, body, reportTypePrefix) {
    return put(`/members-api/irontraps/reports/${reportTypePrefix}/${reportId}/comments`, body)
  },
  fetchForMentionInComment(companyId = '', reportId, type, search, partner, reportTypePrefix) {
    return get(
      `/members-api/irontraps/reports/${reportTypePrefix}/${reportId}/mention/${type}?query=${search}&companyFilter=${companyId}&partner=${partner}`
    )
  },
  saveCommunityUser(nickname) {
    return put(`/members-api/irontraps/community-user?nickname=${nickname}`)
  },
  getScanbackData(companyId = '') {
    return get(`/members-api/irontraps/scanback/overview?companyFilter=${companyId}`)
  },
  fetchAffMailboxesCounts(reportId) {
    return get(`/members-api/irontraps/affmailboxes-counts/${reportId}`)
  },
  getAffectedMailboxData(mitigation_id, companyId = '') {
    return get(
      `/members-api/irontraps/mitigation/${mitigation_id}/details?companyFilter=${companyId}`
    )
  },
  getMitigationData(
    companyFilter = '',
    gmail_message_id,
    office365_message_id,
    message_id,
    recipient_address,
    sender_address,
    subject,
    eid
  ) {
    return get(
      buildURL(`/members-api/irontraps/mitigation/details`, {
        companyFilter,
        gmail_message_id,
        office365_message_id,
        message_id,
        recipient_address,
        sender_address,
        subject,
        eid,
      })
    )
  },
  getCommunityInsights(reportId) {
    return get(`/members-api/community/report-data/${reportId}`)
  },
  getAssignees(companyFilter = '', search = '') {
    return get(
      buildURL('/members-api/irontraps/reports/assignees', {
        companyFilter,
        search,
      })
    )
  },
  changeAssignee(reportId, body, reportTypePrefix) {
    return post(`/members-api/irontraps/reports/${reportTypePrefix}/${reportId}/assign`, body)
  },
  changeAtoAssignee(incidentId, body) {
    return post(`/members-api/irontraps/incidents/${incidentId}/ato-assign`, body)
  },
  fetchScansFreemiumLimit(companyId = '') {
    return get(`/members-api/freemium/scans?companyFilter=${companyId}`)
  },
  fetchRawEmail(reportId, emailType) {
    // validate permissions
    return get(`/members-api/irontraps/reports/${reportId}/${emailType}`)
  },
  downloadOriginalEml(reportId) {
    return get(`/members-api/irontraps/reports/${reportId}/original_eml`, 'blob')
  },
  fetchThreatLogFilters(companyFilter = '') {
    return get(`/members-api/search/investigation/mapping/?companyFilter=${companyFilter}`)
  },
  fetchThreatLogData(body) {
    return post(`/members-api/search/entity_investigation/`, body)
  },
  fetchLogsEntireCount(companyFilter = '', body) {
    return post(
      `/members-api/search/entity_investigation/count/?companyFilter=${companyFilter}`,
      body
    )
  },
  closeIncident(reportId) {
    return post(`/members-api/release-requests-handled/${reportId}`)
  },
  closeSafeChallengeIncident(reportId) {
    return post(`/members-api/safe-challenged-handled/${reportId}`)
  },
  getEmailSnapshot(reportId) {
    return get(`/members-api/irontraps/incidents/mail/snapshot/${reportId}`)
  },
  downloadIndividualEmail(mitigationId) {
    return get(`/members-api/irontraps/reports/${mitigationId}/download_eml`, 'blob')
  },
  releaseEncryptedIncident(reportId) {
    return post(`/members-api/irontraps/reports/encrypted/${reportId}/release`)
  },
  keepEncryptedIncidentInQuarantine(reportId) {
    return post(`/members-api/irontraps/reports/encrypted/${reportId}/approve`)
  },
  fetchPresets() {
    return get(`/members-api/search/investigation/saved_filters/`)
  },
  createPreset(body) {
    return post(`/members-api/search/investigation/saved_filters/`, body)
  },
  updatePreset(body) {
    return put(`/members-api/search/investigation/saved_filters/`, body)
  },
  removePreset(body) {
    return deleteRest(buildURL(`/members-api/search/investigation/saved_filters/`, body))
  },
  releaseFromInvestigation(body, companyId = '') {
    return post(
      `/members-api/search/investigation/release_spam_email/?companyFilter=${companyId}`,
      body
    )
  },
  getReportElements(reportId, body) {
    return post(`/members-api/irontraps/reports-elements/${reportId}/paginated`, body)
  },
  getReportReporters(reportId, page) {
    return get(`/members-api/irontraps/reports/${reportId}/reporters/paginated?page=${page}`)
  },
  /* Challenged-tab "Challenged by" roster. NOT /reporters/paginated: that set also
     holds people who reported before the Safe verdict (co-reporters of the original
     incident, not challengers) and one row per report rather than per person. */
  getReportChallengers(reportId, page) {
    return get(`/members-api/irontraps/reports/${reportId}/challengers/paginated?page=${page}`)
  },
  async getStreamingAnalysis(reportId, reportType, options = {}) {
    const fetchStreamingResponse = async () => {
      // Step 1: Get KMS-encrypted token from Django
      const resp = await lastValueFrom(
        post('/members-api/themis/agent/token', {
          report_id: reportId,
          report_type: reportType,
        })
      )

      if (resp.status !== 200) {
        throw new Error('Failed to get authentication token')
      }
      const tokenResponse = resp.response

      if (!tokenResponse.token) {
        throw new Error('Failed to get authentication token')
      }

      // Step 2: Call microservice directly with token
      const baseUrl = getAgenticBaseUrl(tokenResponse.themisHostname)
      const response = await fetch(`${baseUrl}/api/v1/call-agent-streaming`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${tokenResponse.token}`,
        },
        signal: options.signal,
      })

      // Returned, not thrown: the report epic reads this status to show the
      // out-of-credits screen and refetch billing/status. Rejecting here skips
      // that refetch and leaves the used/total counter stale.
      if (response.status === 402) {
        return response
      }

      if (!response.ok) {
        throw new Error(`Streaming failed: ${response.status} ${response.statusText}`)
      }

      return response // Return streaming response
    }

    // Single attempt only. Retries (bounded + backed off) are owned by the
    // durable per-report budget in redux (THEMIS_MAX_RETRIES) and driven by the
    // Analysis component, so the total number of call-agent-streaming requests
    // per report cannot exceed that budget. Retrying here too would multiply it.
    return fetchStreamingResponse()
  },
  releaseFromIncidentSpam(body, companyId = '') {
    return post(`/members-api/webapp/release-incident-spam/?companyFilter=${companyId}`, body)
  },
  getMSFTQuarantineReport(reportId) {
    return get(`/members-api/irontraps/incidents/msft/${reportId}`)
  },
  releaseMSFTQuarantineReport(reportId, action) {
    return post(`/members-api/irontraps/incidents/msft/${reportId}`, { action })
  },
  releaseMSFTQuarantineReportStatus(taskId) {
    return get(
      buildURL('/members-api/search/investigation/release_email', {
        task_id: taskId,
      })
    )
  },
  updateMSFTQuarantineAssignee(reportId, body) {
    return put(`/members-api/irontraps/incidents/msft/${reportId}`, body)
  },
})
